import React from 'react';
import ReactSelect from 'react-select';
import { FaQuestionCircle } from 'react-icons/fa';
import theme from 'v3/theme';
import Text from '../Text';
import Loader from '../Loader';
import Async from './Async';
import * as Styled from './styles';
import Tooltip from '../Tooltip';

export default function Select({
  label,
  labelTextColor = '#464e5f',
  labelSize = null,
  labelWhiteSpace = 'normal',
  menuFontSize = '14px',
  error,
  placeholder = 'Selecione...',
  onChange,
  value,
  options = [],
  multiple,
  onBlur,
  disableClear = false,
  disableSearch = false,
  isDisabled = false,
  getOptionLabel,
  getOptionValue,
  styles = {},
  components = {},
  noOptionsMessageText = 'Nenhuma opção encontrada',
  loading = false,
  className,
  menuFontColor = '#494950',
  menuStyle,
  tooltip = '',
  tooltipPlacement = 'top',
  tooltipAlign = 'left',
  toolTipWhiteSpace = 'normal',
  closeMenuOnSelect = true,
  openMenuOnFocus = false,
  blurInputOnSelect = true,
  useMenuPortal = false,
  id,
}) {
  const isEmptyObject = (data) =>
    data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0;

  const parsedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : isEmptyObject(value)
      ? null
      : value || null;

  const styledSelect = {
    singleValue: (provided) => ({
      ...provided,
      color: menuFontColor,
      fontSize: menuFontSize,
    }),
    menu: (provided) => ({
      ...provided,
      fontSize: menuFontSize,
      ...menuStyle,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    ...styles,
  };

  return (
    <Styled.Container className={className} style={styles.containerStyle}>
      {label && (
        <div className="d-flex w-100">
          <Styled.Label
            color={labelTextColor}
            type={labelSize ? null : 'label'}
            weight="500"
            $labelWhiteSpace={labelWhiteSpace}
            $labelSize={labelSize}
          >
            {label}
          </Styled.Label>
          {tooltip.length > 0 && (
            <Tooltip
              placement={tooltipPlacement}
              content={
                <Styled.ToolTipLabel
                  $tooltipAlign={tooltipAlign}
                  $toolTipWhiteSpace={toolTipWhiteSpace}
                  type="label"
                  color="white"
                >
                  {tooltip.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </Styled.ToolTipLabel>
              }
            >
              <FaQuestionCircle className="ml-3 mt-1" style={{ color: theme.colors.light_blue, marginLeft: '8px' }} />
            </Tooltip>
          )}
        </div>
      )}

      {loading ? (
        <Loader loading={loading} />
      ) : (
        <>
          <ReactSelect
            styles={styledSelect}
            isDisabled={isDisabled}
            value={parsedValue}
            placeholder={placeholder}
            onChange={onChange}
            options={options}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            isClearable={disableClear ? false : multiple ? parsedValue?.length > 0 : !!parsedValue}
            isSearchable={!disableSearch}
            isMulti={multiple}
            noOptionsMessage={() => <Text>{noOptionsMessageText}</Text>}
            onBlur={onBlur}
            closeMenuOnSelect={closeMenuOnSelect}
            openMenuOnFocus={openMenuOnFocus}
            blurInputOnSelect={blurInputOnSelect}
            components={{
              Control: ({ innerRef, innerProps, children }) => {
                return (
                  <Styled.Control
                    className="select-control"
                    ref={innerRef}
                    {...innerProps}
                    $error={error}
                    $isDisabled={isDisabled}
                    $isMulti={multiple}
                  >
                    {children}
                  </Styled.Control>
                );
              },
              ValueContainer: ({ innerRef, innerProps, children }) => (
                <Styled.ValueContainer ref={innerRef} $isMulti={multiple} {...innerProps}>
                  {children}
                </Styled.ValueContainer>
              ),
              MultiValue: ({ children, components: Components, removeProps }) => (
                <Styled.MultiValue>
                  <Text type="label">{children}</Text>
                  <Styled.MultiValueRemove {...removeProps}>
                    <Components.Remove />
                  </Styled.MultiValueRemove>
                </Styled.MultiValue>
              ),
              Placeholder: ({ innerRef, innerProps, children }) => (
                <Styled.Placeholder ref={innerRef} {...innerProps}>
                  {children}
                </Styled.Placeholder>
              ),
              ...components,
            }}
            inputId={id}
            instanceId={id}
            menuPortalTarget={useMenuPortal ? document.body : undefined}
            menuPosition={useMenuPortal ? 'fixed' : 'absolute'}
          />
          {error && (
            <Styled.ErrorText color="error" type="little">
              {error}
            </Styled.ErrorText>
          )}
        </>
      )}
    </Styled.Container>
  );
}

Select.Async = Async;
